import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { UserPlus, Mail, Users, Trash2, Shield, Eye, Edit3, Copy, X, Building2, AlertCircle } from "lucide-react";

const ROLE_META = {
  admin: { label: "Admin", icon: Shield, hint: "Full workspace access (except billing)." },
  editor: { label: "Editor", icon: Edit3, hint: "Read + edit keywords, competitors, notes." },
  viewer: { label: "Viewer", icon: Eye, hint: "Read-only access to assigned projects." },
};

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [mine, setMine] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", role: "viewer", scoped: false, project_ids: [] });

  const load = async () => {
    try {
      const [m, i, p, w] = await Promise.all([
        api.get("/team/members"),
        api.get("/team/invitations"),
        api.get("/projects"),
        api.get("/team/my-workspaces"),
      ]);
      setMembers(m.data);
      setInvites(i.data);
      setProjects(p.data.filter((x) => x.role === "owner"));
      setMine(w.data);
    } catch (e) { toast.error("Failed to load team data."); }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return toast.error("Enter an email.");
    try {
      await api.post("/team/invitations", {
        email: form.email.trim().toLowerCase(),
        role: form.role,
        project_ids: form.scoped ? form.project_ids : null,
      });
      toast.success("Invitation sent.");
      setForm({ email: "", role: "viewer", scoped: false, project_ids: [] });
      setShowInvite(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Invite failed."); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this pending invitation?")) return;
    await api.delete(`/team/invitations/${id}`);
    toast.success("Invitation revoked.");
    load();
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.member_email} from your workspace? They will lose access immediately.`)) return;
    await api.delete(`/team/members/${m.id}`);
    toast.success("Member removed.");
    load();
  };

  const changeRole = async (m, role) => {
    await api.patch(`/team/members/${m.id}`, { role });
    toast.success(`Role updated to ${role}.`);
    load();
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/join/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Join link copied."));
  };

  const leave = async (w) => {
    if (!window.confirm(`Leave ${w.workspace_owner_email}'s workspace?`)) return;
    await api.post(`/team/leave/${w.id}`);
    toast.success("Left workspace.");
    load();
  };

  const toggleProject = (id) => {
    setForm((f) => ({
      ...f,
      project_ids: f.project_ids.includes(id) ? f.project_ids.filter((x) => x !== id) : [...f.project_ids, id],
    }));
  };

  return (
    <div className="p-8 lg:p-10 max-w-6xl" data-testid="team-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Workspace</div>
          <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Team seats.</h1>
          <p className="mt-2 text-sm text-[#525B6A] max-w-2xl">Invite teammates with per-role, per-project access. Admins can manage everything, editors can add and edit keywords, viewers can only read.</p>
        </div>
        <button onClick={() => setShowInvite(true)} data-testid="team-invite-btn" className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><UserPlus className="h-4 w-4" /> Invite teammate</button>
      </header>

      {showInvite && (
        <form onSubmit={invite} className="mt-6 card-crisp p-6 space-y-4" data-testid="team-invite-form">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">New invitation</div>
              <div className="mt-1 text-lg font-display font-semibold text-[#0A0D14]">Add a teammate</div>
            </div>
            <button type="button" onClick={() => setShowInvite(false)} className="p-1.5 text-[#525B6A]"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Email</label>
              <input required type="email" data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="teammate@agency.com" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Role</label>
              <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                {Object.entries(ROLE_META).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.hint}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="inline-flex items-center gap-2 text-sm text-[#0A0D14] cursor-pointer">
              <input type="checkbox" data-testid="invite-scoped" checked={form.scoped} onChange={(e) => setForm({ ...form, scoped: e.target.checked, project_ids: [] })} />
              Limit to specific projects (unchecked = whole workspace)
            </label>
            {form.scoped && (
              <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                {projects.map((p) => (
                  <label key={p.id} className="inline-flex items-center gap-2 text-xs text-[#0A0D14] cursor-pointer border border-[#E2E8F0] rounded-md px-2 py-1.5">
                    <input type="checkbox" data-testid={`invite-project-${p.id}`} checked={form.project_ids.includes(p.id)} onChange={() => toggleProject(p.id)} />
                    <span>{p.name}<span className="text-[#8A94A6] ml-1">· {p.domain}</span></span>
                  </label>
                ))}
                {projects.length === 0 && <div className="text-xs text-[#8A94A6]">No projects yet.</div>}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button data-testid="invite-submit" className="btn-primary px-4 py-2 rounded-md text-sm">Send invitation</button>
            <button type="button" onClick={() => setShowInvite(false)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          </div>
        </form>
      )}

      {/* Active members */}
      <section className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-[#8A94A6]" />
          <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Active members · {members.length}</div>
        </div>
        <div className="card-crisp overflow-hidden">
          {members.length === 0 && (
            <div className="p-10 text-center text-[#8A94A6] text-sm">No teammates yet. Invite someone to get started.</div>
          )}
          {members.map((m) => (
            <div key={m.id} data-testid={`member-${m.id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 text-sm gap-3">
              <div className="col-span-4">
                <div className="text-[#0A0D14] font-medium">{m.member_name || m.member_email}</div>
                <div className="text-xs text-[#8A94A6]">{m.member_email}</div>
              </div>
              <div className="col-span-3">
                <select data-testid={`member-role-${m.id}`} value={m.role} onChange={(e) => changeRole(m, e.target.value)} className="px-2 py-1 border border-[#E2E8F0] rounded text-xs bg-white">
                  {Object.entries(ROLE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="col-span-3 text-xs text-[#525B6A]">
                {m.project_ids?.length ? `${m.project_ids.length} project${m.project_ids.length === 1 ? '' : 's'}` : "All projects"}
              </div>
              <div className="col-span-2 flex items-center justify-end">
                <button onClick={() => removeMember(m)} data-testid={`member-del-${m.id}`} title="Remove" className="p-1.5 text-[#525B6A] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pending invitations */}
      <section className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-[#8A94A6]" />
          <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Pending invitations</div>
        </div>
        <div className="card-crisp overflow-hidden">
          {invites.filter((i) => i.status === "pending").length === 0 && (
            <div className="p-10 text-center text-[#8A94A6] text-sm">No pending invitations.</div>
          )}
          {invites.filter((i) => i.status === "pending").map((i) => (
            <div key={i.id} data-testid={`invite-${i.id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 text-sm gap-3">
              <div className="col-span-5">
                <div className="text-[#0A0D14] font-medium">{i.email}</div>
                <div className="text-xs text-[#8A94A6]">Invited {new Date(i.invited_at).toLocaleDateString()} · expires {new Date(i.expires_at).toLocaleDateString()}</div>
              </div>
              <div className="col-span-3">
                <span className="pill capitalize">{i.role}</span>
              </div>
              <div className="col-span-2 text-xs text-[#525B6A]">
                {i.project_ids?.length ? `${i.project_ids.length} project${i.project_ids.length === 1 ? '' : 's'}` : "All projects"}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <button onClick={() => copyLink(i.token)} data-testid={`invite-copy-${i.id}`} title="Copy join link" className="p-1.5 text-[#525B6A] hover:text-[#0047FF]"><Copy className="h-3.5 w-3.5" /></button>
                <button onClick={() => revokeInvite(i.id)} data-testid={`invite-revoke-${i.id}`} title="Revoke" className="p-1.5 text-[#525B6A] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Workspaces I'm a member of */}
      {mine.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-[#8A94A6]" />
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Workspaces I&apos;m in</div>
          </div>
          <div className="card-crisp overflow-hidden">
            {mine.map((w) => (
              <div key={w.id} data-testid={`my-ws-${w.id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 text-sm gap-3">
                <div className="col-span-6">
                  <div className="text-[#0A0D14] font-medium">{w.workspace_owner_email}</div>
                  <div className="text-xs text-[#8A94A6]">Joined {new Date(w.joined_at).toLocaleDateString()}</div>
                </div>
                <div className="col-span-3"><span className="pill capitalize">{w.role}</span></div>
                <div className="col-span-3 flex items-center justify-end">
                  <button onClick={() => leave(w)} data-testid={`leave-ws-${w.id}`} className="text-xs text-[#525B6A] hover:text-red-600">Leave workspace</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 inline-flex items-center gap-1.5 text-[11px] text-[#8A94A6]"><AlertCircle className="h-3 w-3" /> Free tier includes 2 teammate seats. Contact us to upgrade.</div>
    </div>
  );
}
